// Segment showing all customers as contacts list, grouped by first letter of
// the customer name.

import { AutoSizer, List }               from 'react-virtualized';
import FilteredList                      from '../components/filtered_list';
import PreventPullToRefreshWhenScrolling from '../components/prevent_pull_to_refresh_when_scrolling';
import React                             from 'react';
import SearchForm                        from '../components/search_form';


export default function AllContacts({ businessID, contacts, isLoadingContacts }) {
  const [ query, setQuery ] = React.useState();

  if (contacts.length === 0)
    return null;

  return (
    <div className="b-segments__container">
      <SearchForm query={query} setQuery={setQuery} placeholder="Search Contacts"/>
      <div className="b-segments">
        <div className={'b-segments__segment b-segments__segment--active'}>
          <FilteredList {...{
            query,
            collection: contacts,
            emptyView:  <NoSearchResults {...{ query, isLoadingContacts }}/>
          }}>
            {filtered => (
              <ContactsList businessID={businessID} contacts={filtered} query={query}/>
            )}
          </FilteredList>
        </div>
      </div>
    </div>
  );
}


function NoSearchResults({ isLoadingContacts, query }) {
  if (isLoadingContacts) {
    return (
      <div className="no-search-results">
        <h3 className="title">Loading...</h3>
        <p className="subtitle">Searching all contacts</p>
      </div>
    );
  } else {
    return (
      <div className="no-search-results">
        <h3 className="title">Nothing found</h3>
        <p className="subtitle">No results found for &quot;{query}&quot;</p>
      </div>
    );
  }
}

class ContactsList extends React.Component {
  constructor(props) {
    super(props);
    this.scrollToLetter   = this.scrollToLetter.bind(this);
    this.getClosestLetter = this.getClosestLetter.bind(this);

    this.state = {
      items:         [],
      scrollToIndex: 0
    };
  }

  static getDerivedStateFromProps(nextProps) {
    const isFiltered = nextProps.query?.length > 0;
    if (isFiltered)
      return { items: nextProps.contacts };
    else {
      const sorted = nextProps.contacts.concat().sort(byIndexLetterAndName);
      const items  = addInitialLettersToContacts(sorted);
      return { items };
    }
  }

  getClosestLetter(letter) {
    const { items } = this.state;

    if (items.includes(letter))
      return letter;
    else {
      const closestLetter = items.find(item => {
        if (typeof item === 'string')
          return item >= letter;
        else
          return false;
      });

      return closestLetter || items.slice(-1);
    }
  }

  scrollToLetter(letter) {
    return event => {
      event.preventDefault();
      const closestLetter      = this.getClosestLetter(letter);
      const closestLetterIndex = this.state.items.findIndex(item => item === closestLetter);

      this.setState({ scrollToIndex: closestLetterIndex });
      this.fixIOSRepaint();
    };
  }

  fixIOSRepaint() {
    // On iOS after scrolling to an upper index the list wasn't visible
    // anymore (still in the DOM though). There is an issue about this:
    // https://github.com/bvaughn/react-virtualized/issues/610
    // This is super hacky but forcing a repaint fixes it
    // More on this:
    // https://martinwolf.org/before-2018/blog/2014/06/force-repaint-of-an-element-with-javascript/

    const isIOS = [ 'iPad', 'iPhone', 'iPod' ].includes(navigator.platform);

    if (isIOS) {
      setTimeout(() => {
        const autoSizerElement         = document.querySelector('.contacts-list > div');
        autoSizerElement.style.display = 'none';
        autoSizerElement.offsetHeight; // eslint-disable-line no-unused-expressions
        autoSizerElement.style.display = 'block';
      }, 0);
    }
  }

  render() {
    const { businessID }    = this.props;
    const { items }         = this.state;
    const { scrollToIndex } = this.state;

    return (
      <div className="list contacts-list">
        {!this.props.query && <QuickLinks onLetterClick={this.scrollToLetter}/>}
        <PreventPullToRefreshWhenScrolling>
          {({ onScroll }) => (
            <AutoSizer>
              {({ width, height }) => (
                <List
                  width={width}
                  height={height}
                  rowCount={items.length}
                  rowHeight={getRowHeightHandler({ items })}
                  rowRenderer={rowRenderHandler({ items, businessID })}
                  scrollToAlignment="start"
                  scrollToIndex={scrollToIndex}
                  onScroll={onScroll}
                />
              )}
            </AutoSizer>
          )}
        </PreventPullToRefreshWhenScrolling>
      </div>
    );
  }
}

function getRowHeightHandler({ items }) {
  return ({ index }) => {
    const isCustomer = !!items[index].id;

    if (isCustomer)
      return 44;
    else
      return 32;
  };
}


function addInitialLettersToContacts(contacts) {
  return contacts.reduce((all, contact) => {
    const last           = all[all.length - 1];
    const lastLetter     = last?.sorting[1];
    const letter         = contact.sorting[1];
    const changingLetter = letter !== lastLetter;
    if (changingLetter)
      all.push(letter);

    all.push(contact);

    return all;
  }, []);
}


function byIndexLetterAndName(a, b) {
  return a.sorting.localeCompare(b.sorting);
}


function rowRenderHandler({ items, businessID }) {
  return ({ key, index, style }) => {
    const item       = items[index];
    const isCustomer = !!item.id;
    if (isCustomer)
      return (<ContactsItem key={key} contact={item} businessID={businessID} style={style}/>);
    else
      return (<LettersItem key={key} letter={item} style={style}/>);
  };
}


function LettersItem({ letter, style }) {
  return (
    <div className="contacts-list__letter list-group-title" id={`letter-${letter}`} style={style}>
      {letter}
    </div>
  );
}


function ContactsItem({ businessID, contact, style }) {
  const href = `/${businessID}/contacts/${contact.id}`;
  return (
    <div className="contacts-list__item item-link" style={style}>
      <a href={href} className="item-content">
        <div className="item-inner">
          <div className="item-title">{contact.name.full}</div>
        </div>
      </a>
    </div>
  );
}

function QuickLinks({ onLetterClick }) {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ#'.split('');

  return (
    <ul className="quick-links">
      {alphabet.map(letter => (
        <li key={letter} onClick={onLetterClick(letter)}>
          <a>
            {letter}
          </a>
        </li>
      ))}
    </ul>
  );
}
